import { useEffect, useState, useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { 
    Button, 
    PageComponent, 
    Skeletons, 
    Table, 
    Modal, 
    Input, 
    MultiSelect, 
    Select,
    Card
} from '../../components';
import { usePermission } from '../../hooks/usePermission';
import Api from '../../Api';
import './style.css';
import { 
    FiActivity, 
    FiPlus, 
    FiTrash2, 
    FiArrowUp, 
    FiArrowDown, 
    FiSend, 
    FiEye,
    FiCheckCircle,
    FiBriefcase,
    FiUsers,
    FiList,
    FiDownload,
    FiCheck
} from 'react-icons/fi';
import * as XLSX from 'xlsx';

// Componente isolado para o conteúdo do formulário para evitar re-renders na tela principal
function RiskMappingFormContent({ companies, employees, onSave, onCancel }) {
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [selectedCompanies, setSelectedCompanies] = useState([]);
    const [selectedEmployees, setSelectedEmployees] = useState([]);
    const [questions, setQuestions] = useState([
        { id: Date.now(), title: '', options: ['', ''], required: true }
    ]);

    const filteredEmployees = useMemo(() => {
        if (selectedCompanies.length === 0) return [];
        return employees.filter(emp => 
            selectedCompanies.includes(String(emp.company_id))
        );
    }, [selectedCompanies, employees]);

    const handleSelectAllEmployees = () => {
        setSelectedEmployees(filteredEmployees.map(e => String(e.id)));
    };

    const handleDeselectAllEmployees = () => {
        setSelectedEmployees([]);
    };

    const addQuestion = () => {
        setQuestions([...questions, { id: Date.now(), title: '', options: ['', ''], required: true }]);
    };

    const removeQuestion = (id) => {
        if (questions.length > 1) {
            setQuestions(questions.filter(q => q.id !== id));
        }
    };

    const updateQuestion = (id, field, value) => {
        setQuestions(questions.map(q => q.id === id ? { ...q, [field]: value } : q));
    };

    const addOption = (questionId) => {
        setQuestions(questions.map(q => {
            if (q.id === questionId) {
                return { ...q, options: [...q.options, ''] };
            }
            return q;
        }));
    };

    const updateOption = (questionId, index, value) => {
        setQuestions(questions.map(q => {
            if (q.id === questionId) {
                const newOptions = [...q.options];
                newOptions[index] = value;
                return { ...q, options: newOptions };
            }
            return q;
        }));
    };

    const removeOption = (questionId, index) => {
        setQuestions(questions.map(q => {
            if (q.id === questionId && q.options.length > 2) {
                const newOptions = q.options.filter((_, i) => i !== index);
                return { ...q, options: newOptions };
            }
            return q;
        }));
    };

    const moveQuestion = (index, direction) => {
        const newQuestions = [...questions];
        const targetIndex = direction === 'up' ? index - 1 : index + 1;
        if (targetIndex >= 0 && targetIndex < newQuestions.length) {
            [newQuestions[index], newQuestions[targetIndex]] = [newQuestions[targetIndex], newQuestions[index]];
            setQuestions(newQuestions);
        }
    };

    const handleSave = () => {
        if (!name || selectedCompanies.length === 0 || selectedEmployees.length === 0 || questions.some(q => !q.title)) {
            toast.warning('Preencha todos os campos obrigatórios.');
            return;
        }

        onSave({
            name,
            description,
            companyIds: selectedCompanies.map(Number),
            employeeIds: selectedEmployees.map(Number),
            formStructure: { questions }
        });
    };

    return (
        <div className="risk-modal-form">
            <div className="risk-form-container">
                <div className="risk-form-section">
                    <h3><FiActivity /> Dados do Mapeamento</h3>
                    <div className="form-row">
                        <div className="form-group" style={{ flex: 2 }}>
                            <Input 
                                label="Nome do Mapeamento" 
                                icon={FiActivity}
                                value={name} 
                                onChange={(e) => setName(e.target.value)} 
                                placeholder="Ex: Mapeamento de Risco Ergonômico 2024"
                                required
                            />
                        </div>
                    </div>
                    <Input 
                        label="Descrição (opcional)" 
                        icon={FiList}
                        value={description} 
                        onChange={(e) => setDescription(e.target.value)} 
                        placeholder="Descreva o objetivo deste mapeamento..."
                    />
                </div>

                <div className="risk-form-section">
                    <h3><FiBriefcase /> Empresas Vinculadas</h3>
                    <MultiSelect 
                        label="Selecione as Empresas"
                        icon={FiBriefcase}
                        options={companies}
                        value={selectedCompanies}
                        onChange={setSelectedCompanies}
                        placeholder="Selecione uma ou mais empresas..."
                    />
                </div>

                <div className="risk-form-section">
                    <h3><FiList /> Construtor de Formulário</h3>
                    <p style={{ fontSize: '0.85rem', color: '#64748b', marginTop: '-0.5rem', marginBottom: '0.5rem' }}>
                        Adicione as perguntas que os colaboradores deverão responder. Todas são de única escolha.
                    </p>
                    
                    {questions.map((q, index) => (
                        <div key={q.id} className="question-item">
                            <div className="question-header">
                                <div style={{ flex: 1 }}>
                                    <Input 
                                        label={`Pergunta ${index + 1}`}
                                        icon={FiActivity}
                                        value={q.title}
                                        onChange={(e) => updateQuestion(q.id, 'title', e.target.value)}
                                        placeholder="Título da pergunta"
                                    />
                                </div>
                                <div className="question-actions">
                                    <Button title="Subir" className="btn-icon btn-outline" onClick={() => moveQuestion(index, 'up')} disabled={index === 0}>
                                        <FiArrowUp size={16} />
                                    </Button>
                                    <Button title="Descer" className="btn-icon btn-outline" onClick={() => moveQuestion(index, 'down')} disabled={index === questions.length - 1}>
                                        <FiArrowDown size={16} />
                                    </Button>
                                    <Button title="Remover Pergunta" className="btn-icon btn-danger" onClick={() => removeQuestion(q.id)}>
                                        <FiTrash2 size={16} />
                                    </Button>
                                </div>
                            </div>

                            <div className="options-container">
                                <label className="input-label" style={{ fontWeight: 600, color: '#475569', marginBottom: '0.75rem', display: 'block' }}>
                                    Alternativas de Resposta
                                </label>
                                {q.options.map((opt, optIndex) => (
                                    <div key={optIndex} className="option-item">
                                        <div style={{ color: '#94a3b8', fontSize: '0.9rem', fontWeight: 600 }}>{optIndex + 1}.</div>
                                        <Input 
                                            value={opt}
                                            onChange={(e) => updateOption(q.id, optIndex, e.target.value)}
                                            placeholder={`Opção ${optIndex + 1}`}
                                        />
                                        <Button 
                                            title="Remover Opção"
                                            className="btn-icon btn-danger btn-small" 
                                            onClick={() => removeOption(q.id, optIndex)}
                                            disabled={q.options.length <= 2}
                                        >
                                            <FiTrash2 size={14} />
                                        </Button>
                                    </div>
                                ))}
                                <Button className="btn-small btn-outline" style={{ marginTop: '0.5rem' }} onClick={() => addOption(q.id)}>
                                    <FiPlus size={14} /> Adicionar Alternativa
                                </Button>
                            </div>

                            <div style={{ marginTop: '1.25rem', padding: '0.5rem', background: '#f1f5f9', borderRadius: '6px' }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', fontSize: '0.9rem', cursor: 'pointer', fontWeight: 500 }}>
                                    <input 
                                        type="checkbox" 
                                        style={{ width: '16px', height: '16px' }}
                                        checked={q.required} 
                                        onChange={(e) => updateQuestion(q.id, 'required', e.target.checked)}
                                    />
                                    Esta pergunta é obrigatória
                                </label>
                            </div>
                        </div>
                    ))}
                    
                    <Button className="add-question-btn btn-outline btn-primary" onClick={addQuestion}>
                        <FiPlus size={18} /> Adicionar Nova Pergunta
                    </Button>
                </div>

                <div className="risk-form-section">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '2px solid #f1f5f9', paddingBottom: '0.75rem', marginBottom: '0.5rem' }}>
                        <h3 style={{ border: 'none', padding: 0, margin: 0 }}><FiUsers /> Colaboradores Vinculados</h3>
                        {selectedCompanies.length > 0 && filteredEmployees.length > 0 && (
                            <div style={{ display: 'flex', gap: '1rem' }}>
                                <button 
                                    type="button" 
                                    onClick={handleSelectAllEmployees}
                                    style={{ background: 'none', border: 'none', color: 'var(--primary-color)', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' }}
                                >
                                    Selecionar Todos
                                </button>
                                <button 
                                    type="button" 
                                    onClick={handleDeselectAllEmployees}
                                    style={{ background: 'none', border: 'none', color: '#ef4444', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' }}
                                >
                                    Desmarcar Todos
                                </button>
                            </div>
                        )}
                    </div>
                    <MultiSelect 
                        label=""
                        icon={FiUsers}
                        options={filteredEmployees.map(e => ({ label: `${e.name} (${e.company?.trade_name || 'N/A'})`, value: String(e.id) }))}
                        value={selectedEmployees}
                        onChange={setSelectedEmployees}
                        placeholder={selectedCompanies.length === 0 ? "Selecione as empresas primeiro" : "Selecione os colaboradores..."}
                        disabled={selectedCompanies.length === 0}
                    />
                    {selectedCompanies.length > 0 && filteredEmployees.length === 0 && (
                        <p style={{ fontSize: '0.85rem', color: '#ef4444' }}>
                            Nenhum colaborador com celular cadastrado foi encontrado para as empresas selecionadas.
                        </p>
                    )}
                </div>
            </div>

            <div className="risk-form-footer">
                <Button className="btn-secondary cancel-button" onClick={onCancel}>Cancelar</Button>
                <Button className="btn-success" onClick={handleSave}>
                    <FiCheckCircle size={18} /> Criar Mapeamento
                </Button>
            </div>
        </div>
    );
}

export default function RiskManager() {
    const navigate = useNavigate();
    const permissions = usePermission('management');
    const [loading, setLoading] = useState(true);
    const [mappings, setMappings] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    
    // Details Modal State
    const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
    const [selectedMappingDetails, setSelectedMappingDetails] = useState(null);
    const [loadingDetails, setLoadingDetails] = useState(false);

    // Delete Modal State
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [mappingToDelete, setMappingToDelete] = useState(null);

    // Start/Resend Modal State
    const [isStartModalOpen, setIsStartModalOpen] = useState(false);
    const [mappingToStart, setMappingToStart] = useState(null);

    // Data for selects
    const [companies, setCompanies] = useState([]);
    const [employees, setEmployees] = useState([]);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        try {
            const [mappingsRes, companiesRes, employeesRes] = await Promise.all([
                Api.get('/risk'),
                Api.get('/companies'),
                Api.get('/employees/all')
            ]);
            setMappings(mappingsRes.data);
            setCompanies(companiesRes.data.map(c => ({ label: c.trade_name || c.corporate_name, value: String(c.id) })));
            setEmployees(employeesRes.data);
        } catch (error) {
            toast.error('Erro ao carregar dados.');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const handleCreateMapping = async (data) => {
        try {
            await Api.post('/risk', data);
            toast.success('Mapeamento criado com sucesso!');
            setIsModalOpen(false);
            loadData();
        } catch (error) {
            toast.error('Erro ao criar mapeamento.');
        }
    };

    const handleStartMapping = (mapping) => {
        setMappingToStart(mapping);
        setIsStartModalOpen(true);
    };

    const confirmStartMapping = async () => {
        if (!mappingToStart) return;

        try {
            const response = await Api.post(`/risk/start/${mappingToStart.id}`);
            if (response.data.error) {
                toast.warning(response.data.error);
            }
            toast.success(response.data.message || 'Mapeamento iniciado! WhatsApps enviados.');
            setIsStartModalOpen(false);
            setMappingToStart(null);
            loadData();
        } catch (error) {
            const errorMsg = error.response?.data?.error || 'Erro ao iniciar mapeamento e enviar WhatsApps.';
            toast.error(errorMsg);
        }
    };

    const handleDelete = (mapping) => {
        setMappingToDelete(mapping);
        setIsDeleteModalOpen(true);
    };

    const confirmDelete = async () => {
        if (!mappingToDelete) return;

        try {
            await Api.delete(`/risk/${mappingToDelete.id}`);
            toast.success('Mapeamento excluído com sucesso.');
            setIsDeleteModalOpen(false);
            setMappingToDelete(null);
            loadData();
        } catch (error) {
            toast.error('Erro ao excluir mapeamento.');
        }
    };

    const handleViewDetails = async (id) => {
        setLoadingDetails(true);
        setIsDetailsModalOpen(true);
        try {
            const response = await Api.get(`/risk/${id}`);
            setSelectedMappingDetails(response.data);
        } catch (error) {
            toast.error('Erro ao buscar detalhes do mapeamento.');
            setIsDetailsModalOpen(false);
        } finally {
            setLoadingDetails(false);
        }
    };

    const handleDownloadReport = async (mapping) => {
        try {
            const response = await Api.get(`/risk/${mapping.id}`);
            const details = response.data;
            
            if (!details.employeeLinks || details.employeeLinks.length === 0) {
                toast.warning('Não há dados para exportar.');
                return;
            }

            const dataToExport = details.employeeLinks.map(link => {
                const row = {
                    'Status': link.status === 'answered' ? 'Respondido' : 'Pendente',
                    'Data de Resposta': link.answered_at ? new Date(link.answered_at).toLocaleDateString() : '-'
                };

                // Add responses as columns
                if (link.responses && details.form?.form_structure?.questions) {
                    details.form.form_structure.questions.forEach(q => {
                        row[q.title] = link.responses[q.id] || '-';
                    });
                }

                return row;
            });

            const ws = XLSX.utils.json_to_sheet(dataToExport);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Respostas_Anonimas');
            XLSX.writeFile(wb, `Relatorio_Anonimo_${mapping.name.replace(/\s+/g, '_')}.xlsx`);
        } catch (error) {
            console.error(error);
            toast.error('Erro ao gerar relatório.');
        }
    };

    const columns = [
        {
            id: 'name',
            header: 'Nome do Mapeamento',
            width: '2fr',
            render: (item) => <strong>{item.name}</strong>,
        },
        {
            id: 'companies',
            header: 'Empresas',
            width: '1fr',
            render: (item) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <FiBriefcase size={14} /> {item.companiesCount}
                </div>
            ),
        },
        {
            id: 'employees',
            header: 'Colaboradores',
            width: '1fr',
            render: (item) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <FiUsers size={14} /> {item.employeesCount}
                </div>
            ),
        },
        {
            id: 'metrics',
            header: 'Métricas',
            width: '1.2fr',
            render: (item) => (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    <div style={{ fontSize: '0.8rem', color: '#64748b' }}>
                        Enviados: <strong>{item.employeesCount}</strong>
                    </div>
                    <div style={{ fontSize: '0.8rem', color: '#2ecc71' }}>
                        Respostas: <strong>{item.answeredCount}</strong>
                    </div>
                </div>
            ),
        },
        {
            id: 'status',
            header: 'Status',
            width: '1fr',
            render: (item) => (
                <span className={`status-badge status-${item.status}`}>
                    {item.status === 'pending' ? 'Pendente' : 
                     item.status === 'active' ? 'Ativo' : 
                     item.status === 'completed' ? 'Concluído' : 'Cancelado'}
                </span>
            ),
        },
        {
            id: 'created_at',
            header: 'Criação',
            width: '1fr',
            render: (item) => new Date(item.created_at).toLocaleDateString(),
        },
        {
            id: 'actions',
            header: 'Ações',
            width: '1.5fr',
            cellClassName: 'manager-table-col-actions',
            render: (item) => (
                <div className="action-buttons">
                    {item.status === 'pending' ? (
                        <Button 
                            title="Iniciar Disparo (WhatsApp)" 
                            onClick={() => handleStartMapping(item)}
                            className="btn-small btn-success"
                        >
                            <FiSend size={16} /> Iniciar
                        </Button>
                    ) : (
                        <Button 
                            title="Reenviar Convites" 
                            onClick={() => handleStartMapping(item)}
                            className="btn-small btn-outline"
                        >
                            <FiSend size={16} />
                        </Button>
                    )}
                    <Button 
                        title="Baixar Relatório" 
                        onClick={() => handleDownloadReport(item)}
                        className="btn-small btn-outline"
                    >
                        <FiDownload size={16} />
                    </Button>
                    <Button 
                        title="Ver Colaboradores" 
                        onClick={() => handleViewDetails(item.id)}
                        className="btn-small btn-primary"
                    >
                        <FiEye size={16} />
                    </Button>
                    <Button 
                        title="Excluir Mapeamento" 
                        onClick={() => handleDelete(item)}
                        className="btn-small btn-danger"
                    >
                        <FiTrash2 size={16} />
                    </Button>
                </div>
            ),
        },
    ];

    function RiskManagerSkeletonWrapper() {
        const { CompanyManagerSkeleton } = Skeletons;
        return <CompanyManagerSkeleton />;
    }

    if (!permissions.canView) {
        return (
            <PageComponent authenticatedPage={true}>
                <div className="container">
                    <div className="manager-header">
                        <div className="manager-header-title">
                            <div className="manager-header-icon">
                                <FiActivity size={22} />
                            </div>
                            <div>
                                <h1>Acesso Negado</h1>
                                <p>Você não tem permissão para visualizar esta página.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </PageComponent>
        );
    }

    return (
        <PageComponent authenticatedPage={true}>
            <div className="container">
                {loading && <RiskManagerSkeletonWrapper />}
                 {!loading && (
                    <>
                        <div className="manager-header">
                            <div className="manager-header-title">
                                <div className="manager-header-icon">
                                <FiActivity size={22} />
                                </div>
                                <div>
                                <h1>Aplicação NR-1</h1>
                                <p>A NR-1 é obrigatória por lei e estabelece as diretrizes básicas de Segurança e Saúde no Trabalho.</p>
                                </div>
                            </div>
                            {permissions.canCreate && (
                                <Button onClick={() => setIsModalOpen(true)} className="manager-header-button">
                                    <FiPlus size={18} />
                                    Mapeamento
                                </Button>
                            )}
                        </div>

                        <Table 
                            items={mappings} 
                            columns={columns} 
                            getRowKey={(item) => item.id}
                            totalItems={mappings.length}
                            emptyMessage="Nenhum mapeamento de risco encontrado."
                        />
                    </>
                 )}

                <Modal 
                    isOpen={isModalOpen} 
                    onClose={() => setIsModalOpen(false)} 
                    title="Novo Mapeamento de Risco"
                    size="lg"
                    closeOnOverlayClick={false}
                >
                    <RiskMappingFormContent 
                        companies={companies} 
                        employees={employees} 
                        onSave={handleCreateMapping} 
                        onCancel={() => setIsModalOpen(false)} 
                    />
                </Modal>

                <Modal 
                    isOpen={isDetailsModalOpen} 
                    onClose={() => setIsDetailsModalOpen(false)} 
                    title="Detalhes do Mapeamento"
                    size="lg"
                >
                    {loadingDetails ? (
                        <div style={{ padding: '2rem', textAlign: 'center' }}>
                            <FiActivity className="spin" size={32} />
                            <p>Carregando colaboradores...</p>
                        </div>
                    ) : selectedMappingDetails ? (
                        <div style={{ padding: '1rem' }}>
                            <div style={{ marginBottom: '1.5rem' }}>
                                <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--primary-color)' }}>
                                    {selectedMappingDetails.name}
                                </h2>
                                <p style={{ color: '#64748b' }}>{selectedMappingDetails.description || 'Sem descrição'}</p>
                            </div>

                            <div style={{ maxHeight: '50vh', overflowY: 'auto' }}>
                                <Table 
                                    items={selectedMappingDetails.employeeLinks || []}
                                    getRowKey={(link) => link.id}
                                    totalItems={selectedMappingDetails.employeeLinks?.length || 0}
                                    columns={[
                                        {
                                            id: 'employee',
                                            header: 'Colaborador',
                                            render: (link) => (
                                                <div>
                                                    <strong>{link.employee?.name}</strong>
                                                    <div style={{ fontSize: '0.8rem', color: '#64748b' }}>
                                                        {link.employee?.company?.trade_name}
                                                    </div>
                                                </div>
                                            )
                                        },
                                        {
                                            id: 'status',
                                            header: 'Status',
                                            render: (link) => (
                                                <span 
                                                    className={`manager-tag ${link.status === 'answered' ? 'manager-tag-success' : 'manager-tag-warning'}`}
                                                    style={{ padding: '4px 8px', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 600 }}
                                                >
                                                    {link.status === 'answered' ? 'Respondido' : 'Aguardando Resposta'}
                                                </span>
                                            )
                                        },
                                        {
                                            id: 'sent_at',
                                            header: 'Data Envio',
                                            render: (link) => {
                                                if (!link.sent_at) return '-';
                                                try {
                                                    const date = new Date(link.sent_at);
                                                    return isNaN(date.getTime()) ? '-' : date.toLocaleDateString();
                                                } catch (e) {
                                                    return '-';
                                                }
                                            }
                                        },
                                        {
                                            id: 'answered_at',
                                            header: 'Data Resposta',
                                            render: (link) => {
                                                if (!link.answered_at) return '-';
                                                try {
                                                    const date = new Date(link.answered_at);
                                                    return isNaN(date.getTime()) ? '-' : date.toLocaleDateString();
                                                } catch (e) {
                                                    return '-';
                                                }
                                            }
                                        }
                                    ]}
                                    emptyMessage="Nenhum colaborador vinculado."
                                />
                            </div>
                        </div>
                    ) : null}
                    <div className="risk-form-footer">
                        <Button className="btn-secondary" onClick={() => setIsDetailsModalOpen(false)}>Fechar</Button>
                    </div>
                </Modal>

                <Modal 
                    isOpen={isDeleteModalOpen} 
                    onClose={() => setIsDeleteModalOpen(false)} 
                    title="Confirmar Exclusão"
                    size="sm"
                >
                    <div style={{ padding: '1.5rem', textAlign: 'center' }}>
                        <div style={{ 
                            width: '60px', 
                            height: '60px', 
                            borderRadius: '50%', 
                            backgroundColor: '#fee2e2', 
                            color: '#ef4444', 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center', 
                            margin: '0 auto 1.5rem' 
                        }}>
                            <FiTrash2 size={30} />
                        </div>
                        <h3 style={{ fontSize: '1.2rem', fontWeight: 700, marginBottom: '0.75rem', color: '#1e293b' }}>
                            Excluir Mapeamento?
                        </h3>
                        <p style={{ color: '#64748b', lineHeight: '1.5', marginBottom: '1.5rem' }}>
                            Tem certeza que deseja excluir <strong>{mappingToDelete?.name}</strong>?<br />
                            Esta ação é permanente e removerá todas as respostas e vínculos associados.
                        </p>
                        <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
                            <Button className="btn-secondary" style={{ flex: 1 }} onClick={() => setIsDeleteModalOpen(false)}>
                                Cancelar
                            </Button>
                            <Button className="btn-danger" style={{ flex: 1 }} onClick={confirmDelete}>
                                Sim, Excluir
                            </Button>
                        </div>
                    </div>
                </Modal>

                <Modal 
                    isOpen={isStartModalOpen} 
                    onClose={() => setIsStartModalOpen(false)} 
                    title={mappingToStart?.status === 'pending' ? "Iniciar Mapeamento" : "Reenviar Convites"}
                    size="sm"
                >
                    <div style={{ padding: '1.5rem', textAlign: 'center' }}>
                        <div style={{ 
                            width: '60px', 
                            height: '60px', 
                            borderRadius: '50%', 
                            backgroundColor: '#dcfce7', 
                            color: '#22c55e', 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center', 
                            margin: '0 auto 1.5rem' 
                        }}>
                            <FiSend size={30} />
                        </div>
                        <h3 style={{ fontSize: '1.2rem', fontWeight: 700, marginBottom: '0.75rem', color: '#1e293b' }}>
                            {mappingToStart?.status === 'pending' ? "Deseja iniciar o mapeamento?" : "Deseja reenviar os convites?"}
                        </h3>
                        <p style={{ color: '#64748b', lineHeight: '1.5', marginBottom: '1.5rem' }}>
                            {mappingToStart?.status === 'pending' 
                                ? `Isso enviará uma mensagem via WhatsApp para todos os colaboradores vinculados a "${mappingToStart?.name}".`
                                : `Isso reenviará a mensagem via WhatsApp apenas para os colaboradores que ainda não responderam a "${mappingToStart?.name}".`}
                        </p>
                        <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
                            <Button className="btn-secondary" style={{ flex: 1 }} onClick={() => setIsStartModalOpen(false)}>
                                Cancelar
                            </Button>
                            <Button className="btn-success" style={{ flex: 1 }} onClick={confirmStartMapping}>
                                Confirmar Envio
                            </Button>
                        </div>
                    </div>
                </Modal>
            </div>
        </PageComponent>
    )

}
